import { FreeData, FreeDataArgs, FreeDataValue } from '@wizeoo/akm-message-lib';
import { DATA_KIND } from '../../model/enums';
import { FreedataArgsVO } from './freedataargs.vo';
import { FreedataValueVO } from './freedatavalue.vo';
import { field } from '../entity-maps';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { Model } from '../../model/model';

/**
 * VO représentant une freedata
 * Réduit à son minimum pour utilisation par le moteur
 */
export class FreeDataVO {

    /** Le modèle pour l'accés au parentVO */
    protected _model: Model;
    public get model(): Model {
        return this._model;
    }

    constructor(model: Model, pSource?: FreeData) {
        this._model = model; 
        this._source = null;
        if (pSource) {
            this._source = pSource;
        } else {
            this._source = new FreeData();
            this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
        }
    }

    /** L'objet source provenant du back */
    protected _source: FreeData; // FD source

    public set source(src: FreeData) {
        this._source = src;
    }
    public get source(): FreeData {
        return this._source;
    }

    /** Domaine */
    public set domain(domain: string) {
        this._source.domain = domain;
    }
    public get domain(): string {
        return this._source.domain;
    }

    /** BU */
    public set businessUnitCode(businessUnitCode: string) {
        this._source.businessUnitCode = businessUnitCode;
    }
    public get businessUnitCode(): string {
        return this._source.businessUnitCode;
    }

    public set code(code: string) {
        this._source.code = code;
    }
    public get code(): string {
        return this._source.code;
    }

    public set dataType(value: string) {
        this._source.dataType = value;
    }
    public get dataType(): string {
        return this._source.dataType;
    }

    @field('string')
    public set defaultValue(value: string) {
      this.source.defaultValue = value;
    }
    public get defaultValue(): string {
      return this.source.defaultValue;
    }
  
    @field('string')
    public set defaultTrigger(value: string) {
      this.source.defaultTrigger = value;
    }
    public get defaultTrigger(): string {
      return this.source.defaultTrigger;
    }

    @field('boolean')
    public set kindMeasure(value: boolean) {
      this.source.kindMeasure = value;
    }
    public get kindMeasure(): boolean {
      return this.source.kindMeasure;
    }
  
    @field('boolean')
    public set kindWild(value: boolean) {
      this.source.kindWild = value;
    }
    public get kindWild(): boolean {
      return this.source.kindWild;
    }


    /** le FD possède une valeur "numérique" (pour les charts et calculs) */
    public dataTypeIsNumeric(): boolean {
        return this.dataType === DATA_KIND.NUMBER || this.dataType === DATA_KIND.INT || this.dataType === DATA_KIND.DATE;
    }

    private _valuesVOMap: Map<string, FreedataValueVO> = null; // map par code, pour accès rapide

    protected get valuesVOMap(): Map<string, FreedataValueVO> {
        if (!this._valuesVOMap) {
            this._computeValues();
        }
        return this._valuesVOMap;
    }

    private _parentValuesVOMap: Map<string, FreedataValueVO[]> = null; // map par code parent, pour fonction keylist

    protected get parentValuesVOMap(): Map<string, FreedataValueVO[]> {
        if (!this._parentValuesVOMap) {
            this._computeValues();
        }
        return this._parentValuesVOMap;
    }

    private _valuesVO: FreedataValueVO[] = null; // liste de FDV encapsulés en VO

    protected get valuesVO(): FreedataValueVO[] {
        if (!this._valuesVO) {
            this._computeValues();
        }
        return this._valuesVO;
    }
    protected set valuesVO(values: FreedataValueVO[]) {
        this._source.freeDataValues = {};
        values.map((item) => {
            this._source.freeDataValues[item.code] = item.source;
        });
        this._computeValues();
    }

    private _argsVO: FreedataArgsVO[] = null; // les args en collection
    private _argsVOMap: Map<string, FreedataArgsVO> = null; // map des arguments, par code de champ

    protected get argsVO(): FreedataArgsVO[] {
        if ((!this._argsVO)) {
            if (!this._source.args) {
                this._source.args = {};
            }
            this._argsVO = Object.values(this._source.args).map((arg: FreeDataArgs) => this.model.freedataEngine.newFreedataArgsVO(arg));
        }
        return this._argsVO;
    }
    protected set argsVO(values: FreedataArgsVO[]) {
        this._source.args = {};
        values.map((item) => {
            this._source.args[item.code] = item.source;
        });
    }

    protected get argsVOMap(): Map<string, FreedataArgsVO> {
        if (!this._argsVOMap) {
            this._argsVOMap = new Map(this.argsVO.map(item => [item.code, item]));
        }
        return this._argsVOMap;
    }

    private _computeValues() {
        if (!this._source.freeDataValues) {
            this._source.freeDataValues = {};
        }
        this._valuesVOMap = new Map();
        this._parentValuesVOMap = new Map();


        this._valuesVO = Object.values(this._source.freeDataValues).map((fdv: FreeDataValue) => {
            const fdvvo = this.model.freedataEngine.newFreedataValueVO(fdv); // création d'un freedatavalueVO
            this._valuesVOMap.set(fdvvo.code, fdvvo); // construction de la map par code

            if (!this._parentValuesVOMap.has(fdvvo.parentCode)) { // construction de la map par code parent
                this._parentValuesVOMap.set(fdvvo.parentCode, []);
            }
            this._parentValuesVOMap.get(fdvvo.parentCode).push(fdvvo);

            return fdvvo;
        });
    }

    public resetCollections(): void {
        this._valuesVOMap = null;
        this._valuesVO = null;
        this._parentValuesVOMap = null;

        this._argsVO = null;
        this._argsVOMap = null;
    }

    /** ajout d'un FDV sur le Freedata */
    public addFreeDataValue(pValue: FreedataValueVO): void {
        if (this.valuesVOMap?.has(pValue.code)) { // déjà présent... C'est un update
            this.valuesVOMap.get(pValue.code).source = pValue.source;
        } else {
            this.valuesVO.push(pValue);
            if (!this._source.freeDataValues) {
                this._source.freeDataValues = {};
            }
            // ajout à la map, qui sert pour les contrôles d'unicité
            this.valuesVOMap.set(pValue.code, pValue);
        }

        this._source.freeDataValues[pValue.code] = pValue.source; // maj de la source

    }

    /** suppression d'un FDV sur le Freedata */
    public removeFreeDataValue(toDelete: FreedataValueVO): void {

        this.valuesVOMap.delete(toDelete.code);
        delete this._source.freeDataValues[toDelete.code];

        // this._valuesVO = [...this.valuesVOMap.values()];
        this.valuesVO.forEach((value, index) => {
            if (value.code === toDelete.code) { this.valuesVO.splice(index, 1); }
        });

    }

    /* ############################################
     *       Collecte/Construction des VOs
     * ############################################ */

    /** collecte des FDVos */
    public getValuesVO(): FreedataValueVO[] {
        return this.valuesVO;
    }

    /** collecte d'un FDVvo par son code */
    public getFreeDataValue(pCode: string): FreedataValueVO {
        return this.valuesVOMap.get(pCode);
    }

    /** collecte des FDVos par code parent */
    public getFreeDataValueByParent(parentCode: string): FreedataValueVO[] {
        return this.parentValuesVOMap.get(parentCode);
    }

    /** collecte par shortCode */
    public getFreeDataValueByShortCode(pShortCode: string): FreedataValueVO[] {
        return this.valuesVO.filter(item => item.shortCode === pShortCode);
    }

    /** collecte des ArgsVO */
    public getArgsVO(): FreedataArgsVO[] {
        return this.argsVO;
    }

    /** collecte des ArgsVO sous forme de Map */
    public getArgsVOMap(): Map<string, FreedataArgsVO> {
        return this.argsVOMap;
    }

  // map d'updates codeFDV = [FDV, EM_ACTION]
  protected _updateMap: Map<string, [FreedataValueVO, string]> = new Map<string, [FreedataValueVO, string]>();
  public get updateMap(): Map<string, [FreedataValueVO, string]> {
    return this._updateMap;
  }

  /** refresh de la map depuis l'extérieur (formWidget et Excel Engine) */
  public resetUpdateMap(map: Map<string, [FreedataValueVO, string]> = null): void {
    // console.log('qui est responsable ??');
    if (map) {
      this._updateMap = map;
    } else {
      this._updateMap = new Map<string, [FreedataValueVO, string]>();
    }
  }

}
